import {
  ActionRow,
  TovButton,
  DetailList,
  PageCard,
  PageEyebrow,
  PageHero,
  Select,
  SelectOption,
  TovField,
  TovFormTextField,
  TovFormTextareaField,
  useTovForm,
  msg,
} from '@tov-plus/ui';

import { type guestApi } from '../app/api';

type RegistryView = Awaited<ReturnType<typeof guestApi.getRegistry>>;
type RegistryClaimFormValues = {
  quantity: string;
  confirmationNote: string;
};

export function RegistryHero({ registry }: { registry: RegistryView }) {
  return (
    <PageHero>
      <PageEyebrow>{msg('general.modules.registry.name')}</PageEyebrow>
      <h2>
        {msg(
          'pages.registry_page_sections.pages_registry_page_sections_claim_gifts_without_duplicate_handoffs',
        )}
      </h2>
      <p>
        {msg(
          'pages.registry_page_sections.pages_registry_page_sections_filter_for_what_is_still_available_claim_what',
        )}{' '}
      </p>
      <DetailList>
        <span>
          <strong>{registry.items.length}</strong>{' '}
          {msg('pages.gallery_page.pages_gallery_page_visible_items')}{' '}
        </span>
        <span>
          <strong>{registry.filters.availableCount}</strong>{' '}
          {msg(
            'pages.registry_page_sections.pages_registry_page_sections_still_available',
          )}{' '}
        </span>
        <span>
          <strong>{registry.filters.claimedCount}</strong>{' '}
          {msg(
            'pages.registry_page_sections.pages_registry_page_sections_already_claimed_by_you',
          )}{' '}
        </span>
      </DetailList>
    </PageHero>
  );
}

export function RegistryFilterCard({
  availabilityFilter,
  onAvailabilityFilterChange,
}: {
  availabilityFilter: 'all' | 'available';
  onAvailabilityFilterChange(nextValue: 'all' | 'available'): void;
}) {
  return (
    <PageCard wide>
      <TovField
        label={msg(
          'pages.registry_page_sections.pages_registry_page_sections_filter',
        )}
      >
        <Select
          value={availabilityFilter}
          onChange={(event) =>
            onAvailabilityFilterChange(
              event.target.value as 'all' | 'available',
            )
          }
        >
          <SelectOption value="available">
            {msg(
              'pages.registry_page_sections.pages_registry_page_sections_available_only',
            )}
          </SelectOption>
          <SelectOption value="all">
            {msg(
              'pages.registry_page_sections.pages_registry_page_sections_all_items',
            )}
          </SelectOption>
        </Select>
      </TovField>
    </PageCard>
  );
}

export function RegistryItemCard({
  entry,
  onClaim,
  onUnclaim,
}: {
  entry: RegistryView['items'][number];
  onClaim(input: { quantity: number; confirmationNote: string | null }): void;
  onUnclaim(): void;
}) {
  function parseQuantity(value: string) {
    const normalizedValue = value.trim();
    if (normalizedValue.length === 0) {
      return 1;
    }

    const parsed = Number(normalizedValue);
    return Number.isFinite(parsed)
      ? Math.min(entry.item.quantity, Math.max(1, parsed))
      : 1;
  }

  return (
    <PageCard wide>
      <PageEyebrow>
        {entry.item.claimMode.replace('_', ' ')}{' '}
        {msg('pages.registry_page_sections.pages_registry_page_sections_claim')}
      </PageEyebrow>
      <h2>{entry.item.name}</h2>
      <p>
        {entry.item.description ??
          msg(
            'pages.registry_page_sections.pages_registry_page_sections_no_additional_gift_details_were_provided',
          )}
      </p>
      <DetailList>
        <span>
          <strong>{entry.analytics.remainingQuantity}</strong>{' '}
          {msg(
            'pages.registry_page_sections.pages_registry_page_sections_remaining',
          )}
        </span>
        <span>
          <strong>{entry.analytics.claimedQuantity}</strong>{' '}
          {msg(
            'pages.registry_page_sections.pages_registry_page_sections_already_claimed',
          )}{' '}
        </span>
        <span>
          <strong>{entry.analytics.claimantCount}</strong>{' '}
          {msg(
            'pages.registry_page_sections.pages_registry_page_sections_claimant_s',
          )}{' '}
        </span>
      </DetailList>
      {entry.item.link ? (
        <a
          className="secondary-link"
          href={entry.item.link}
          rel="noreferrer"
          target="_blank"
        >
          {msg(
            'pages.registry_page_sections.pages_registry_page_sections_open_item_link',
          )}{' '}
        </a>
      ) : null}
      <RegistryClaimForm
        entry={entry}
        onClaim={(values) =>
          onClaim({
            quantity: parseQuantity(values.quantity),
            confirmationNote: values.confirmationNote.trim() || null,
          })
        }
        onUnclaim={onUnclaim}
      />
    </PageCard>
  );
}

function RegistryClaimForm({
  entry,
  onClaim,
  onUnclaim,
}: {
  entry: RegistryView['items'][number];
  onClaim(values: RegistryClaimFormValues): void;
  onUnclaim(): void;
}) {
  const form = useTovForm({
    defaultValues: {
      quantity: String(entry.myClaim?.quantity ?? 1),
      confirmationNote: entry.myClaim?.confirmationNote ?? '',
    } satisfies RegistryClaimFormValues,
    onSubmit: ({ value }) => onClaim(value),
  });

  return (
    <form
      className="detail-list detail-list--stacked"
      onSubmit={async (event) => {
        event.preventDefault();
        await form.handleSubmit();
      }}
    >
      <TovFormTextField
        form={form}
        label={msg('general.fields.quantity.label')}
        max={entry.item.quantity}
        min={1}
        name="quantity"
        type="number"
      />
      <TovFormTextareaField
        form={form}
        label={msg(
          'pages.shared_planning_page.pages_shared_planning_page_note',
        )}
        name="confirmationNote"
      />
      <ActionRow>
        <TovButton type="submit">
          {entry.myClaim
            ? msg(
                'pages.registry_page_sections.pages_registry_page_sections_update_claim',
              )
            : msg(
                'pages.shared_planning_page.pages_shared_planning_page_claim_item',
              )}
        </TovButton>
        {entry.myClaim ? (
          <TovButton onClick={onUnclaim} type="button" variant="secondary">
            {msg(
              'pages.registry_page_sections.pages_registry_page_sections_release_claim',
            )}{' '}
          </TovButton>
        ) : null}
      </ActionRow>
    </form>
  );
}
